import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Component, computed, effect, inject, input, model, OnDestroy, OnInit, signal } from '@angular/core';
import { CHART_DATA_STATUS_PUBLISHER } from '@shared/di/chart-data-status-publisher.token';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { AbstractBaseComponent } from '@shared/abstracts/abstract-base.component';
import { SkeletonModule } from 'primeng/skeleton';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faInfoCircle, faSadTear } from '@fortawesome/free-solid-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { EnCardVariant } from '@shared/enums/card-variant.enum';
import { ITrend } from '@shared/components/trend/trend.interface';
import { Observable } from 'rxjs';
import { InfoCardComponent } from '../info-card/info-card.component';
import { DecimalPipe, NgClass, PercentPipe } from '@angular/common';
import { TrendComponent } from '@shared/components/trend/trend.component';
import {
  METRIC_CARD_ICON_DEFAULT_SEVERITY,
  METRIC_CARD_ICON_SEVERITY_STYLES,
  MetricCardIconSeverity,
} from './metric-card.constants';

@Component({
  selector: 'app-metric-card',
  imports: [
    TagModule,
    TooltipModule,
    SkeletonModule,
    FontAwesomeModule,
    DecimalPipe,
    PercentPipe,
    NgClass,
    InfoCardComponent,
    TrendComponent,
  ],
  // No self-provided COMPONENT_STATE: the card inherits the ambient state from
  // its host page's provider, so the built-in skeleton (`@if (state.loading())`)
  // reflects the page's fetch state (e.g. driven by `@HandleEffectLoading`).
  template: `
    <div [id]="uuid">
      @if (state.loading()) {
        <app-info-card>
          <div class="card-label">
            <p-skeleton width="14rem" height="1.25rem"/>
          </div>
          <div class="card-icon">
            <p-skeleton size="2.5rem" shape="circle"/>
          </div>
          <div class="card-value flex flex-row justify-start items-center h-16 mt-1">
            <p-skeleton width="5rem" height="2.5rem"/>
          </div>
          <div class="card-footer">
            <p-skeleton width="18rem" height="1rem"/>
          </div>
        </app-info-card>
      } @else {

        @if (variant() === EnCardVariant.DISABLED) {
          <app-info-card [id]="id()" [clickable]="false">
            <div class="card-label"><small i18n class="block text-muted-color">Dato non disponibile</small></div>
            <div class="card-icon">
              <div class="bg-gray-100 dark:bg-gray-400/10 rounded-border">
                <fa-icon
                  class="fa-circle-info size-10 text-center flex justify-center items-center text-gray-400/80 dark:text-gray-100"
                  [pTooltip]="tooltip()" [icon]="faSadTear"></fa-icon>
              </div>
            </div>
            <div class="card-value">
              <div class="bg-gray-100 dark:bg-gray-400/10 rounded-border w-[4rem] h-[4rem] my-5"></div>
            </div>
            <div class="card-footer"></div>
          </app-info-card>
        }

        @if (variant() !== EnCardVariant.DISABLED) {
          <app-info-card [id]="id()" [clickable]="clickable()" (click)="onClick($event)">
            <div class="card-label"><span class="block text-500 font-medium mb-3">{{ text() }}</span></div>
            <div class="card-icon">
              <div class="rounded-border flex items-center justify-center size-10"
                   [ngClass]="METRIC_CARD_ICON_SEVERITY_STYLES[iconSeverity()].container">
                @if (iconPrime()) {
                  <i class="text-xl" [ngClass]="[iconPrime(), METRIC_CARD_ICON_SEVERITY_STYLES[iconSeverity()].color]"
                     [pTooltip]="tooltip()"></i>
                } @else {
                  <fa-icon class="fa-circle-info size-10 text-center flex justify-center items-center"
                           [ngClass]="METRIC_CARD_ICON_SEVERITY_STYLES[iconSeverity()].color" [pTooltip]="tooltip()"
                           [icon]="displayIcon()"></fa-icon>
                }
              </div>
            </div>
            <div class="card-value">
              <span class="font-medium text-5xl">
                @if (data() !== undefined && dataValue() !== undefined) {
                  @if (percentage()) {
                    {{ dataValue() | percent : digitsInfo() }}
                  } @else {
                    {{ dataValue() | number : digitsInfo() }}
                  }
                  @if (valueSuffix()) {
                    <span class="text-3xl text-muted-color">{{ valueSuffix() }}</span>
                  }
                } @else {
                  <span class="text-muted-color">--</span>
                }
              </span>
            </div>
            <div class="card-footer">
              @if (trend()) {
                <app-trend
                  [delta]="delta()"
                  [days]="days()"
                  [current]="current()"
                  [previous]="previous()"
                  [suffix]="suffix()"
                  [higher]="trendHigher()">
                </app-trend>
              }
              @if (tip()) {
                <small class="text-muted-color">{{ tip() }}</small>
              }
              @if (archivedValue() !== undefined && archivedValue()! > 0) {
                <small class="text-muted-color" i18n>{{ archivedValue() }} archiviati</small>
              }
            </div>
          </app-info-card>
        }
      }
    </div>
  `,
  styles: `
    @use "sass:color";

    :host {
      display: block;

      fa-icon.fa-face-sad-tear {
        font-size: 1.5rem;
      }

      fa-icon.fa-circle-info {
        font-size: 1.5em;
      }
    }
  `,
})
export class MetricCardComponent extends AbstractBaseComponent implements OnInit, OnDestroy {
  /** Optional: only set when this card is hosted under a page that provides it (see the interface's own doc). */
  private readonly statusPublisher = inject(CHART_DATA_STATUS_PUBLISHER, { optional: true });

  /**
   * Represents a data model that can hold either a number or an Observable emitting number.
   */
  data = model<number | Observable<number>>();

  /**
   * Represents the count of soft-deleted (archived) items associated with the metric.
   * When provided, it displays a muted secondary count in the card footer (e.g. "3 archiviati").
   */
  archived = input<number | Observable<number>>();

  /**
   * Represents the numeric value displayed that may either be a number or undefined.
   */
  protected dataValue = signal<number | undefined>(undefined);

  /**
   * Represents the resolved numeric value of the archived count signal.
   */
  protected archivedValue = signal<number | undefined>(undefined);

  /**
   * Represents the text input provided by the user that will be used to display the label of the card.
   */
  text = input<string>();

  /**
   * A reactive signal that represents the current card variant state,
   * initialized with the base card variant. This signal can be used
   * to dynamically track and update the variant type of card component
   * in the application.
   */
  variant = signal<EnCardVariant>(EnCardVariant.BASE);

  /**
   * Represents the clickable state of an element or component.
   * This variable determines whether the associated element is interactive
   * and can respond to user click events.
   */
  clickable = input<boolean>(false);

  // sets digit and decimal representation.
  digitsInfo = input<string>('1.0-3');

  /**
   * A variable that represents a percentage setting.
   * This boolean value determines whether a specific feature or functionality
   * related to percentage calculations is enabled or disabled.
   *
   * - `true`: The percentage functionality is enabled.
   * - `false`: The percentage functionality is disabled.
   *
   * The default value for this variable is `false`.
   */
  percentage = input<boolean>(false);

  /**
   * Represents the denominator in a mathematical operation, which is the divisor in a division operation.
   *
   * The value of the denominator is an input of type `number`. It is initialized to 0 by default.
   * In mathematical contexts, the denominator should not be zero to avoid division by zero errors.
   *
   * Usage Note:
   * Ensure to validate the denominator value before performing division operations
   * to prevent runtime errors or undefined behavior.
   *
   * Type: number
   * Default Value: 0
   */
  denominator = input<number>(0);

  /**
   * Represents a text label that appears as a tooltip, providing additional information
   * when users hover over or focus on an associated user interface element.
   */
  tooltip = input<string>();

  /**
   * Optional custom icon shown in the top-right corner. Falls back to the
   * default info icon when not provided.
   */
  icon = input<IconDefinition>();

  /** PrimeIcons class (e.g. `pi pi-directions`). Takes precedence over {@link icon}. */
  iconPrime = input<string>();

  /**
   * Optional free-form text rendered at the bottom of the card (e.g. a note
   * shown instead of, or alongside, the trend).
   */
  tip = input<string>();

  /** Optional suffix rendered next to the main value (e.g. `/10`). */
  valueSuffix = input<string>();

  /** Icon actually rendered: the custom {@link icon} or the default info icon. */
  displayIcon = computed<IconDefinition>(() => this.icon() ?? this.faInfoCircle);

  /**
   * Icon badge severity (PrimeNG-style). `info` = blue (default), `primary` = grey.
   */
  iconSeverity = input<MetricCardIconSeverity>(METRIC_CARD_ICON_DEFAULT_SEVERITY);

  /**
   * Represents a trend object.
   *
   * This variable is used to associate or store a trend-related input of type ITrend.
   * The ITrend type defines the structure of the data associated with the trend.
   */
  trend = input<ITrend>();

  /** When false, a decrease is treated as positive (e.g. pain KPI). */
  trendHigher = input<boolean>(true);

  /**
   * A computed property that evaluates and returns the `delta` value from the `trend` object.
   * If the `trend` object is not available or undefined, this property will return `undefined`.
   */
  delta = computed(() => this._getTrendProperty(trend => trend.delta));

  /**
   * A computed property that retrieves the number of days associated with the current trend.
   *
   * This property calculates its value dynamically based on the current state of the `trend`.
   * If `trend` is not available, it will return undefined.
   */
  days = computed(() => this._getTrendProperty(trend => trend.days));

  /**
   * A computed property that retrieves the current value from the trend data.
   *
   * The `current` variable holds the result of a computed function. It dynamically calculates
   * the current value by invoking the `trend()` method. If the `trend()` method does not return
   * any data, the value of `current` will be `undefined`.
   *
   * This property is used to provide a reactive and up-to-date reference to the current value
   * from the trend object.
   */
  current = computed(() => this._getTrendProperty(trend => trend.current));

  /**
   * A computed property that retrieves the `previous` value from the `trend` object.
   *
   * This property computes its value dynamically based on the current state of the `trend` object.
   * If the `trend` is not defined, the computed value will be `undefined`.
   * Otherwise, it will resolve to the `previous` property of the `trend` object.
   */
  previous = computed(() => this._getTrendProperty(trend => trend.previous));

  /**
   * A computed property that retrieves the suffix of the current trend.
   * If no trend is available, it returns undefined.
   */
  suffix = computed(() => this._getTrendProperty(trend => trend.suffix));

  constructor() {
    super();
    effect(() => {
      if (this.error) this.variant.set(EnCardVariant.DISABLED);
    });
    /** Reports "do I have a value to show" — a card still showing "--" never counts as having data. */
    effect(() => {
      this.statusPublisher?.reportStatus(this.uuid, this.dataValue() !== undefined);
    });
  }

  ngOnDestroy(): void {
    this.statusPublisher?.clearStatus(this.uuid);
  }

  ngOnInit() {
    const data = this.data();
    if (data instanceof Observable) {
      data.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value: number) => {
        this.dataValue.set(value);
        this.variant.set(this._handleInternalCardState(value));
      });
    }
    if (typeof data === 'number') {
      this.dataValue.set(data);
      this.variant.set(this._handleInternalCardState(data));
    }

    const archived = this.archived();
    if (archived instanceof Observable) {
      archived.subscribe((value: number) => this.archivedValue.set(value));
    }
    if (typeof archived === 'number') {
      this.archivedValue.set(archived);
    }
  }

  protected onClick(event: Event): void {
    const clickable = this.clickable();
    const loading = this.state.loading();
    const variant = this.variant();
    if (variant === EnCardVariant.DISABLED || loading || !clickable) {
      event.stopImmediatePropagation();
      return;
    }
  }

  private _handleInternalCardState(value: number): EnCardVariant {
    const denominator = this.denominator();
    if (!!value && !!denominator) {
      const average = ((value / denominator) * 100) === Infinity ? -1 : (value / denominator) * 100;
      this.logger.debug(this.constructor.name, this.text() ?? '-', `(${value}/${denominator})*100 = ${average}`);
      if (average === 0) {
        return EnCardVariant.POSITIVE;
      } else if (average > 0 && average <= 20) {
        return EnCardVariant.CAUTION;
      } else if (average > 20) {
        return EnCardVariant.CRITICAL;
      }
    }
    return EnCardVariant.BASE;
  }

  /**
   * Extracts a property from the trend object with null checking.
   * @param propertyExtractor Function to extract the desired property from the trend
   * @returns The property value or undefined if trend is not available
   */
  private _getTrendProperty<T>(propertyExtractor: (trend: ITrend) => T): T | undefined {
    const trend = this.trend();
    return trend ? propertyExtractor(trend) : undefined;
  }


  protected readonly EnCardVariant = EnCardVariant;
  protected readonly METRIC_CARD_ICON_SEVERITY_STYLES = METRIC_CARD_ICON_SEVERITY_STYLES;
  protected readonly faSadTear = faSadTear;
  protected readonly faInfoCircle = faInfoCircle;
}
